import {
    useState,
    useEffect,
    useCallback,
    useRef,
    startTransition,
    lazy,
    type FC,
    type FocusEvent as ReactFocusEvent,
    type FormEvent,
    type KeyboardEvent,
    type MouseEvent,
} from 'react';
import { useRefetchableFragment, useFragment, graphql } from 'react-relay';
import classNames from 'classnames';
import { useIntl, defineMessages } from 'dibs-react-intl';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import { useImageSearch } from '../../hooks/useImageSearch.ts';
const ImageSearchModal = lazy(
    () =>
        import(
            /* webpackChunkName: "ImageSearchModal" */ 'dibs-image-search/exports/ImageSearchModal.ts'
        )
);
const { EVENT_NAVIGATION } = eventNameConstants;
const { INTERACTION_TYPE_GLOBAL_NAV } = interactionTypeConstants;
const {
    STEP_INTERACTION_GLOBAL_NAV_SEARCH_BAR_CLICK,
    STEP_INTERACTION_GLOBAL_NAV_SEARCH_BAR_ROW_CLICK,
    STEP_INTERACTION_GLOBAL_NAV_SEARCH_BAR_SUBMIT_CLICK,
} = stepInteractionConstants;
import { SearchBar as SearchBarElement } from 'dibs-elements/exports/SearchBar.tsx';
import { SIZES } from 'dibs-elements/exports/constants.ts';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

import { SearchBarOverlay } from './SearchBarOverlay.tsx';
import { useSearchSuggestions } from '../../hooks/useSearchSuggestions.ts';
import { useSearchBarTracking } from '../../hooks/useSearchBarTracking.ts';
import {
    getCursorTerms,
    handleRecentStorage,
    noop,
    type CursorTerm,
} from '../../utils/sharedSearchBarHelpers.ts';
import {
    KEY_DOWN_VALUE,
    KEY_UP_VALUE,
    SEARCH_PAGE_PATH,
    SEARCH_QUERY_PARAM,
    ORIGINAL_SEARCH_QUERY_PARAM,
    SEARCH_BAR_TRACKING,
    SEARCH_SUGGESTION_INCLUDE_TYPES,
} from '../../utils/sharedSearchBarConstants.ts';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';

import dibsCss from 'dibs-css';

import { type SearchBar_viewer$key } from './__generated__/SearchBar_viewer.graphql.ts';
import { type SearchBar_user$key } from './__generated__/SearchBar_user.graphql.ts';
import { type SearchBarRefetchQuery } from './__generated__/SearchBarRefetchQuery.graphql.ts';

const MIN_NUM_VALUE_CHARACTERS = 2;
const SEARCH_BAR_DATA_TN = 'global-header-search-input';

const ELEMENTS_PREVENTING_SEARCH_BAR_SHRINK = [
    `${SEARCH_BAR_DATA_TN}-search-button`,
    `${SEARCH_BAR_DATA_TN}-clear`,
    // in some OS + browsers combos buttons are not focused on click, then tabbable wrapper div gets the focus
    `${SEARCH_BAR_DATA_TN}-search-button-wrapper`,
    `${SEARCH_BAR_DATA_TN}-clear-wrapper`,
];

const labels = defineMessages({
    searchPlaceholder: {
        id: 'dbl.Header.SearchBar.searchPlaceholder',
        defaultMessage: 'Search 1stDibs',
    },
});

const refetchableViewerFragment = graphql`
    fragment SearchBar_viewer on Viewer
    @argumentDefinitions(
        query: { type: "String", defaultValue: "" }
        hasQuery: { type: "Boolean", defaultValue: false }
    )
    @refetchable(queryName: "SearchBarRefetchQuery") {
        ...useSearchSuggestions_viewer @arguments(query: $query, hasQuery: $hasQuery)
    }
`;

const userFragment = graphql`
    fragment SearchBar_user on User {
        ...ImageSearchModal_user
        ...getRecentHistory_user @defer
    }
`;

type SearchBarProps = {
    viewer: SearchBar_viewer$key;
    user: SearchBar_user$key | null | undefined;
    hasRootPendingRefetch: boolean;
    isClient: boolean;
};

export const SearchBar: FC<SearchBarProps> = ({
    viewer: viewerRef,
    user: userRef,
    hasRootPendingRefetch,
    isClient,
}) => {
    const intl = useIntl();
    const [value, setValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isSearchBarCleared, setIsSearchBarCleared] = useState(false);
    const [cursorTerms, setCursorTerms] = useState<CursorTerm[]>([]);
    const [cursorPosition, setCursorPosition] = useState(-1);
    const [cursorHighlightId, setCursorHighlightId] = useState<string | undefined>(undefined);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const overlayWrapper = useRef<HTMLDivElement | null>(null);

    const [viewer, refetch] = useRefetchableFragment<SearchBarRefetchQuery, SearchBar_viewer$key>(
        refetchableViewerFragment,
        viewerRef
    );
    const user = useFragment(userFragment, userRef);

    const { suggestions, recentSearches, trackEventLabel } = useSearchSuggestions({ viewer });

    const dropdownChoices = suggestions || recentSearches;
    const isVisible = isFocused || isHovered;

    // Close overlay when focus moves outside of the SearchBar wrapper (e.g., via Tab navigation)
    useEffect(() => {
        function handleOutsideFocus(e: FocusEvent): void {
            if (!wrapperRef?.current) {
                return;
            }
            if (isFocused && e.target instanceof Node && !wrapperRef.current?.contains(e.target)) {
                setIsFocused(false);
                setIsHovered(false);
            }
        }

        if (isFocused) {
            document.body.addEventListener('focus', handleOutsideFocus, true);
        }
        return () => {
            document.body.removeEventListener('focus', handleOutsideFocus, true);
        };
    }, [isFocused]);

    const onFocus = useCallback((event: ReactFocusEvent<HTMLInputElement>) => {
        trackEvent(
            {
                category: SEARCH_BAR_TRACKING.CATEGORIES.NAVIGATION,
                action: SEARCH_BAR_TRACKING.ACTIONS.SEARCH_BAR_CLICK,
                eventName: EVENT_NAVIGATION,
                interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
                step_interaction_name: STEP_INTERACTION_GLOBAL_NAV_SEARCH_BAR_CLICK,
            },
            event
        );
        setIsFocused(true);
    }, []);

    const onBlur = useCallback((event: ReactFocusEvent<HTMLInputElement>) => {
        const focusedElement = event.relatedTarget?.getAttribute('data-tn');
        if (!focusedElement || !ELEMENTS_PREVENTING_SEARCH_BAR_SHRINK.includes(focusedElement)) {
            setIsFocused(false);
        }
    }, []);

    const onClear = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setIsFocused(false);
    }, []);

    const handleRowClick = useCallback(
        (val: string, href: string, event: MouseEvent | FormEvent) => {
            setValue(val);
            setIsFocused(false);
            setIsHovered(false);

            trackEvent(
                {
                    category: SEARCH_BAR_TRACKING.CATEGORIES.NAVIGATION,
                    action: SEARCH_BAR_TRACKING.ACTIONS.HEADER_NAV_CLICK,
                    label: trackEventLabel,
                    eventName: EVENT_NAVIGATION,
                    interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
                    step_interaction_name: STEP_INTERACTION_GLOBAL_NAV_SEARCH_BAR_ROW_CLICK,
                    trigger: trackEventLabel,
                },
                event
            );

            handleRecentStorage(val, href);
            location.assign(handleLocaleUrl(href, GLOBAL_CLIENT_ONLY_LOCALE));
        },
        [trackEventLabel]
    );

    const handleCursorChange = useCallback(
        (move: typeof KEY_UP_VALUE | typeof KEY_DOWN_VALUE) => {
            const newCursorTerms = cursorTerms.length
                ? cursorTerms
                : getCursorTerms(dropdownChoices, value, intl);
            let newCursorPosition = cursorPosition;

            if (!dropdownChoices) {
                // if we have no section values exit out
                return;
            }

            switch (move) {
                case KEY_UP_VALUE:
                    newCursorPosition =
                        cursorPosition || cursorPosition === 0
                            ? (cursorPosition - 1 + newCursorTerms.length) % newCursorTerms.length
                            : newCursorTerms.length - 2;
                    break;
                case KEY_DOWN_VALUE:
                    newCursorPosition =
                        cursorPosition || cursorPosition === 0
                            ? (cursorPosition + 1) % newCursorTerms.length
                            : 0;
                    break;
                default:
                    break;
            }

            const newValue = newCursorTerms[newCursorPosition].term;
            const newHighlightId = newCursorTerms[newCursorPosition].highlightId;
            setValue(newValue);
            setCursorHighlightId(newHighlightId);
            setCursorTerms(newCursorTerms);
            setCursorPosition(newCursorPosition);
        },
        [dropdownChoices, value, intl, cursorPosition, cursorTerms]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>): void => {
            if (event.key === 'Escape') {
                setIsFocused(false);
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                handleCursorChange(KEY_DOWN_VALUE);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                handleCursorChange(KEY_UP_VALUE);
            }
        },
        [handleCursorChange]
    );

    const fetchSuggestions = useCallback(
        (searchQuery: string) => {
            if (!hasRootPendingRefetch) {
                startTransition(() => {
                    refetch(
                        {
                            hasQuery: true,
                            query: searchQuery.toLowerCase(),
                            includeTypes: SEARCH_SUGGESTION_INCLUDE_TYPES,
                        },
                        { onComplete: () => setIsSearchBarCleared(false) }
                    );
                });
            }
        },
        [refetch, hasRootPendingRefetch]
    );

    const [debouncedFetchSuggestions] = useDebouncedCallback(fetchSuggestions, 500);

    const handleChange = useCallback(
        (newValue: string) => {
            setValue(newValue);
            if (newValue) {
                debouncedFetchSuggestions(newValue);
            } else {
                setIsSearchBarCleared(true);
            }
            setCursorTerms([]);
            setCursorHighlightId(undefined);
            setCursorPosition(-1);
        },
        [debouncedFetchSuggestions]
    );

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            let urlLabel: string | undefined;

            if (cursorPosition > -1 && cursorTerms[cursorPosition].link) {
                handleRowClick(
                    cursorTerms[cursorPosition].term,
                    cursorTerms[cursorPosition].link,
                    event
                );
            } else if (value) {
                urlLabel = `/search/?q=${encodeURIComponent(value.toLowerCase())}`;
                trackEvent(
                    {
                        category: SEARCH_BAR_TRACKING.CATEGORIES.NAVIGATION,
                        action: SEARCH_BAR_TRACKING.ACTIONS.HEADER_NAV_CLICK,
                        label: SEARCH_BAR_TRACKING.LABELS.GLOBAL_SEARCH,
                        eventName: EVENT_NAVIGATION,
                        interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
                        step_interaction_name: STEP_INTERACTION_GLOBAL_NAV_SEARCH_BAR_SUBMIT_CLICK,
                        trigger: SEARCH_BAR_TRACKING.LABELS.GLOBAL_SEARCH,
                    },
                    event
                );
                handleRecentStorage(value, urlLabel);
                location.assign(handleLocaleUrl(urlLabel, GLOBAL_CLIENT_ONLY_LOCALE));
            }
        },
        [value, cursorTerms, cursorPosition, handleRowClick]
    );

    useEffect(() => {
        const { hash, href, search } = location;
        const hashParams = new URLSearchParams(hash?.split('?')?.[1] || '');
        const oqParam = hashParams.get(ORIGINAL_SEARCH_QUERY_PARAM);

        if (href.indexOf(SEARCH_PAGE_PATH) !== -1 || oqParam) {
            const searchParams = new URLSearchParams(search);
            const searchTerm = oqParam || searchParams.get(SEARCH_QUERY_PARAM);

            if (searchTerm) {
                setValue(searchTerm);
            }
        }
    }, []);

    useEffect(() => {
        const el = overlayWrapper.current;
        if (!el) {
            return undefined;
        }
        const onMouseOver = (): void => setIsHovered(true);
        const onMouseOut = (): void => setIsHovered(false);
        el.addEventListener('mouseover', onMouseOver);
        el.addEventListener('mouseout', onMouseOut);
        return () => {
            el.removeEventListener('mouseover', onMouseOver);
            el.removeEventListener('mouseout', onMouseOut);
        };
    }, []);

    const showRecentlyViewed =
        !suggestions || value.length < MIN_NUM_VALUE_CHARACTERS || isSearchBarCleared;

    useSearchBarTracking({ isVisible, showRecentlyViewed, recentSearches, suggestions });
    const { isImageSearchModalOpen, openImageSearchModal, closeImageSearchModal } =
        useImageSearch();

    return (
        <div className={classNames(dibsCss.relative, dibsCss.zNone)} ref={wrapperRef}>
            <form
                className={classNames(dibsCss.flex, dibsCss.m0, dibsCss.p0)}
                action="/search/"
                role="search"
                onSubmit={handleSubmit}
            >
                <div className={dibsCss.wFull}>
                    <SearchBarElement
                        dataTn={SEARCH_BAR_DATA_TN}
                        isClearButtonHidden={!value}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        onChange={handleChange}
                        onClear={onClear}
                        onKeyDown={handleKeyDown}
                        placeholder={intl.formatMessage(labels.searchPlaceholder)}
                        size={SIZES.mediumLarge}
                        value={value}
                        leftMargin={SIZES.medium}
                        isHeaderSearch
                        openImageSearchModal={openImageSearchModal}
                    />
                </div>
            </form>
            <div
                className={classNames(dibsCss.absolute, dibsCss.wFull, dibsCss.zBelow)}
                ref={overlayWrapper}
            >
                {isClient ? (
                    <SearchBarOverlay
                        canShowRecentlyViewed={isVisible && showRecentlyViewed}
                        handleRowClick={handleRowClick}
                        visible={isVisible}
                        suggestions={showRecentlyViewed ? recentSearches : suggestions}
                        searchTerm={value}
                        cursorHighlightId={cursorHighlightId}
                        user={user || null}
                    />
                ) : null}
            </div>
            {isImageSearchModalOpen && (
                <ImageSearchModal user={user} closeModal={closeImageSearchModal || noop} />
            )}
        </div>
    );
};
